/**
 * Moya product gallery — basic client-side wishlist toggle (localStorage-backed).
 * Loaded via snippets/product-gallery.liquid.
 *
 * NOTE: this only persists saved product IDs in the visitor's browser (per
 * device, no account/cross-device sync). It does not build a "wishlist page"
 * listing saved products — that would need a dedicated template that reads
 * this same storage key and fetches product data to display.
 */

/**
 * On mobile, the wishlist heart moves from the gallery's top-right corner
 * to sit next to the product title instead (snippets/product-info.liquid
 * renders an empty `.product-wishlist-slot` there for this). This
 * physically re-parents the SAME <wishlist-button> element rather than
 * rendering a second one, so there's only ever one bound click listener
 * and one piece of state to keep in sync — no duplicate-instance drift.
 *
 * Moving an already-connected element to a new parent in the same
 * document does not re-trigger connectedCallback/disconnectedCallback, so
 * the button's existing listener and state survive the move untouched.
 */
function initWishlistPlacement() {
	// Scoped to the main product section, not just document-wide — other
	// product-gallery instances can exist on the same page (featured
	// product, quick buy), and a bare querySelector would grab whichever
	// one happens to be first in the DOM.
	const mainProductSection = document.querySelector(".shopify-section--main-product");

	if (!mainProductSection) {
		return;
	}

	const wishlistButton = mainProductSection.querySelector(".product-gallery__wishlist-button");
	const mobileSlot = mainProductSection.querySelector(".product-wishlist-slot");
	const galleryImageList = mainProductSection.querySelector(".product-gallery__image-list");

	if (!wishlistButton || !mobileSlot || !galleryImageList) {
		return;
	}

	const mobileQuery = window.matchMedia("(max-width: 699px)");

	const placeWishlistButton = () => {
		if (mobileQuery.matches) {
			mobileSlot.append(wishlistButton);
		} else {
			galleryImageList.before(wishlistButton);
		}
	};

	placeWishlistButton();
	mobileQuery.addEventListener("change", placeWishlistButton);
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initWishlistPlacement);
} else {
	initWishlistPlacement();
}

class WishlistButton extends HTMLElement {
	static STORAGE_KEY = "moya:wishlist";

	connectedCallback() {
		this.productId = this.getAttribute("product-id");
		this.button = this.querySelector("button");

		if (!this.productId || !this.button) {
			return;
		}

		this.updateState();
		this.button.addEventListener("click", () => this.toggle());
	}

	getSavedIds() {
		try {
			const raw = localStorage.getItem(WishlistButton.STORAGE_KEY);
			const parsed = raw ? JSON.parse(raw) : [];
			return Array.isArray(parsed) ? parsed : [];
		} catch (error) {
			return [];
		}
	}

	isSaved() {
		return this.getSavedIds().includes(this.productId);
	}

	toggle() {
		const ids = this.getSavedIds();
		const index = ids.indexOf(this.productId);

		if (index === -1) {
			ids.push(this.productId);
		} else {
			ids.splice(index, 1);
		}

		localStorage.setItem(WishlistButton.STORAGE_KEY, JSON.stringify(ids));
		this.updateState();
	}

	updateState() {
		const saved = this.isSaved();
		this.button.setAttribute("aria-pressed", String(saved));
		this.classList.toggle("is-active", saved);
	}
}

if (!window.customElements.get("wishlist-button")) {
	window.customElements.define("wishlist-button", WishlistButton);
}
